
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import UserLayout from '../../../components/layouts/UserLayout'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { authHeader, parseNigerianStates, slugify } from '../../../utils';
import { fetchPayments } from '../../../store/actions/paymentsActions';
import axios from 'axios';
import { ERROR, SET_SUCCESS } from '../../../store/types';
import Loader from '../../../components/elements/Loader';
import CheckIcon from '../../../components/elements/icons/CheckIcon';
import TextField from '../../../components/elements/form/TextField';
import RadioGroup from '../../../components/elements/form/RadioGroup';
import DateField from '../../../components/elements/form/DateField';
import moment from 'moment';
import AutocompleteSelect from '../../../components/elements/form/AutocompleteSelect';
import { Nationalities } from '../../../assets/static/nationalities';
import { StatesLgas } from '../../../assets/static/stateslgas';
import ArrowIcon from '../../../components/elements/icons/ArrowIcon';
import FormButton from '../../../components/elements/form/FormButton';
import { clearCreatedApplication, clearUpdatedApplication, createApplication, updateApplication } from '../../../store/actions/applicationsActions';
import NumberField from '../../../components/elements/form/NumberField';
import PlusIcon from '../../../components/elements/icons/PlusIcon';
import TrashIcon from '../../../components/elements/icons/TrashIcon';
import Checkbox from '../../../components/elements/form/Checkbox';
import FileUpload from '../../../components/elements/form/FileUpload';
import SelectField from '../../../components/elements/form/SelectField';
import { fetchExaminationCenters } from '../../../store/actions/examinationCentersActions';
import DocumentSvgIcon from '../../../assets/images/document-icon.svg'
import LeftRightArrow from '../../../components/elements/icons/LeftRightArrow';
import CloseIcon from '../../../components/elements/icons/CloseIcon';
import TextareaField from '../../../components/elements/form/TextareaField';

const NewApplication = () => {
    const { admissionId } = useParams()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const applicationsSelector = useSelector((state => state.applications))
    const examinationCentersSelector = useSelector((state => state.examinationCenters))
    const paymentsSelector = useSelector((state => state.payments))
    const [searchParams] = useSearchParams()
    // eslint-disable-next-line no-unused-vars
    const [applicationId, setApplicationId] = useState(searchParams.get("application"));

    const [loading, setLoading] = useState(true);   
    const [admission, setAdmission] = useState(null);
    const [applicationPayload, setApplicationPayload] = useState({});

    const applicationSteps = useMemo(() => [
        {
            label: "Applicant's Data",
            slug: 'biodata',
            description: 'Provide personal information about the applicant, including name, gender, nationality, and other relevant details.',
            requiredFields: ['firstName', 'lastName', 'gender', 'nationality', 'state', 'lga', 'class', 'complexion', 'religion', 'languages', 'tribe', 'placeOfBirth']
        },
        {
            label: 'Family Details',
            slug: 'family-details',
            description: 'Enter details about the applicant’s family, such as mother’s maiden name and number of siblings.',
            requiredFields: ['mothersMaidenName']
        },
        {
            label: 'Activities & Interests',
            slug: 'activities-interests',
            description: 'Share information on the applicant’s extracurricular activities, sports interests, and hobbies.',
            requiredFields: ['sportsInterests', 'hobbies'],
            skippable: true
        },
        {
            label: 'Schools Attended',
            slug: 'schools-attended',
            description: 'List all schools the applicant has attended, including names and dates of attendance.',
            requiredFields: ['schoolsAttended']
        },
        {
            label: 'Documentation',
            slug: 'documentation',
            description: 'Upload necessary documents, such as a passport photograph, to complete the application.',
            requiredFields: ['passportPhotograph']
        },
    ], []);

    const checkRequiredFields = (requiredFields, application) => {
        return requiredFields?.every(field => {
            return (
                application.hasOwnProperty(field) && // Check if key exists
                application[field] !== '' && // Check if key is not an empty string
                (!Array.isArray(application[field]) || // Check if key is not an empty array
                (Array.isArray(application[field]) && application[field].length > 0)) // Check if array has elements
            );
        });
    };
    
    const fetchApplication = useCallback(async () => {
        try {
            const headers = authHeader();
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/applications/${applicationId}?expand=admission,payment,scheduledExaminations.examination`, { headers });
    
            setApplicationPayload(response.data.data);
            setLanguages(response.data.data?.languages?.length > 0 ? response.data.data.languages : [''])
            setSportsInterests(response.data.data?.sportsInterests?.length > 0 ? response.data.data?.sportsInterests : [''])
            setHobbies(response.data.data?.hobbies?.length > 0 ? response.data.data?.hobbies : [''])
            setSchools(response.data.data?.schoolsAttended?.length > 0 ? response.data.data?.schoolsAttended : [school])
            if(response.data.data?.nationality === 'Nigeria' && response.data.data?.state){
                const selectedStateLgas = StatesLgas.find((state)=>{
                    return state.stateSlug === slugify(response.data.data?.state)
                })?.lgas
                setActiveLgas(selectedStateLgas)
        
                setTimeout(() => {
                    setLgasEnabled(true)
                }, 50);
            }
            setApplicantFather(response.data.data?.applicantFather || parentSchema)
            setApplicantMother(response.data.data?.applicantMother || parentSchema)
            setHasVcmSiblings(response.data.data?.vcmSiblings && response.data.data?.vcmSiblings?.length > 0 ? true : false)
            setVcmSiblings(response.data.data.vcmSiblings && response.data.data.vcmSiblings.length > 0 ? response.data.data.vcmSiblings : [vcmSibling])

            setSportsInterests(response.data.data.sportsInterests && response.data.data.sportsInterests.length > 0 ? response.data.data.sportsInterests : [''])
    
            // Update completed steps based on the current state
            setCompletedSteps(prevCompletedSteps => {
                return applicationSteps.reduce((updatedCompletedSteps, step, stepIndex) => {
                    const allFieldsExist = checkRequiredFields(step.requiredFields, response.data.data);
                    if (allFieldsExist) {
                        updatedCompletedSteps.push(stepIndex);
                        setActiveStep(stepIndex+1)
                    }
                    // if (!allFieldsExist && step.skippable) {
                    //     // updatedCompletedSteps.push(stepIndex);
                    //     setActiveStep(stepIndex)
                    // }
                    // if (allFieldsExist && step.skippable) {
                    //     updatedCompletedSteps.push(stepIndex);
                    //     setActiveStep(stepIndex+1)
                    // }
                    return updatedCompletedSteps;
                }, [...prevCompletedSteps]);
            });
    
            setLoading(false);
        } catch (error) {
            console.log(error);
            dispatch({
                type: ERROR,
                error
            });
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationId, applicationSteps, dispatch]);
    
    const [entryClasses, setEntryClasses] = useState([]);

    useEffect(() => {
        dispatch(fetchExaminationCenters('', 0, 0))
        const fetchAdmission = async () => {
            try {
            const headers = authHeader()
    
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/admissions/${admissionId}?expand=fee,academicSession`, { headers })            
    
            setAdmission(response.data.data)
            setEntryClasses(response.data.data.entryClasses.map(entryClass => {
                return {
                    label: entryClass,
                    value: entryClass
                };
            }));

            if(response.data.data.fee){
                dispatch(fetchPayments(`fee=${response.data.data.fee._id}&applied=false`))
            }

            if(applicationId && applicationId !== '') {
                fetchApplication()
                // setLoading(false)
            } else {
                setLoading(false)
            }
    
            } catch (error) {
            console.log(error)
            dispatch({
                type: ERROR,
                error
            })
            setLoading(false)
            }
        }

        fetchAdmission()

        if (applicationsSelector.createdApplication !== null) {

            // setApplicationId(applicationsSelector.createdApplication._id)
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.set('application', applicationsSelector.createdApplication._id);
            window.location.href = newUrl.toString();
            setTimeout(() => {
                dispatch(clearCreatedApplication)
            }, 200);
            setActiveStep(activeStep + 1)
        }

        if (applicationsSelector.updatedApplication !== null) {
            setTimeout(() => {
                dispatch(clearUpdatedApplication)
            }, 200);

            if(applicationCompleted){

                dispatch({
                    type: SET_SUCCESS,
                    payload: 'Application Submitted successfully!'
                })
                navigate('/user/applications')
            }
            setActiveStep(activeStep + 1)
        }
        
        return () => {
            
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [admissionId, dispatch, applicationsSelector.createdApplication, applicationsSelector.updatedApplication]);


    const [completedSteps, setCompletedSteps] = useState([]);
    const [activeStep, setActiveStep] = useState(0);

    const [validationErrors, setValidationErrors] = useState({});

    const [activeLgas, setActiveLgas] = useState([])
    const [lgasEnabled, setLgasEnabled] = useState(false)

    const selectState = (selectedState) => {
        setLgasEnabled(false)
        const selectedStateLgas = StatesLgas.find((state)=>{
            return state.stateSlug === slugify(selectedState.label)
        })?.lgas

        console.log(selectedStateLgas)
        setActiveLgas(selectedStateLgas)

        setTimeout(() => {
            setLgasEnabled(true)
        }, 50);

        setApplicationPayload({...applicationPayload, ...{state: selectedState.label}})
    }

    const validateStep1Form = () => {
        let errors = {}
        
        if(!applicationPayload.firstName || applicationPayload.firstName === '') {
            errors.firstName = true
        }
        
        if(!applicationPayload.lastName || applicationPayload.lastName === '') {
            errors.lastName = true
        }
        
        if(!applicationPayload.dob || applicationPayload.dob === '') {
            errors.dob = true
        }
        
        if(!applicationPayload.gender || applicationPayload.gender === '') {
            errors.gender = true
        }
        
        if(!applicationPayload.complexion || applicationPayload.complexion === '') {
            errors.complexion = true
        }
        
        if(!applicationPayload.religion || applicationPayload.religion === '') {
            errors.religion = true
        }
        
        if(!applicationPayload.nationality || applicationPayload.nationality === '') {
            errors.nationality = true
        }
        
        if(!applicationPayload.tribe || applicationPayload.tribe === '') {
            errors.tribe = true
        }
        
        if(!applicationPayload.placeOfBirth || applicationPayload.placeOfBirth === '') {
            errors.placeOfBirth = true
        }
        
        if(!applicationPayload.state || applicationPayload.state === '') {
            errors.state = true
        }
        
        if(!applicationPayload.lga || applicationPayload.lga === '') {
            errors.lga = true
        }
        
        if(!applicationPayload.class || applicationPayload.class === '') {
            errors.class = true
        }

        if(!languages || (languages.length === 1 && languages[0] === '')) {
            errors.languages = true
            // setValidationErrors(errors)
            // return errors
        } else {
            languages.forEach((language, languageIndex)=>{
                if(language === ''){
                    errors[`languages-${languageIndex}`] = true
                }
            })
        }

        setValidationErrors(errors)
        return errors
    }

    const completeStepOne = async () => {
        try {
            if (Object.values(validateStep1Form()).includes(true)) {
    
                dispatch({
                    type: ERROR,
                    error: {response: {data: {
                        message: 'Please check the highlighted fields'
                    }}}
                })
                return
            }

            const payload = {
                ...applicationPayload, 
                ...{
                    admission: admission._id,
                    languages: languages
                }
            }

            if(admission.fee && (!applicationId || applicationId === null)){
                payload.payment = paymentsSelector.payments.payments[0]._id
            }

            // do this if this is an application in review, update the application and not create a new one
            if(applicationId || applicationId!== null){ 
                dispatch(updateApplication(applicationId, payload))
                return
            }

            dispatch(createApplication(payload))

        } catch (error) {
            console.log(error)
            dispatch({
                type: ERROR,
                error
            })
            setLoading(false)
        }
        
    }

    const vcmSibling = {
        name: '',
        studentCode: '',
    }

    const [hasVcmSiblings, setHasVcmSiblings] = useState(applicationPayload?.vcmSiblings?.length > 0 ? true : false);

    const [vcmSiblings, setVcmSiblings] = useState([vcmSibling]);

    const addVcmSibling = () => {
        let tempSiblings = [...vcmSiblings]
        tempSiblings.push(vcmSibling)

        setVcmSiblings(tempSiblings)
    }

    const updateVcmSibling = (index, field, value) => {
        let tempSiblings = [...vcmSiblings]
        tempSiblings[index][field] = value

        setVcmSiblings(tempSiblings)
    }

    const removeVcmSibling = (index) => {
        let tempSiblings = [...vcmSiblings]
        tempSiblings.splice(index, 1)

        setVcmSiblings(tempSiblings)
    }

    const parentSchema = {
        name: '',
        occupation: '',
        homeAddress: '',
        workAddress: ''
    }

    const [applicantMother, setApplicantMother] = useState(applicationPayload?.applicantMother || parentSchema);
    const [applicantFather, setApplicantFather] = useState(applicationPayload?.applicantFather || parentSchema);

    const validateStep2Form = () => {
        let errors = {}
        
        if(!applicationPayload.mothersMaidenName || applicationPayload.mothersMaidenName === '') {
            errors.mothersMaidenName = true
        }
        
        // if(!applicationPayload.numberOfSiblings || applicationPayload.numberOfSiblings === '') {
        //     errors.numberOfSiblings = true
        // }
        
        if(!applicationPayload.parentRelationship || applicationPayload.parentRelationship === '') {
            errors.relationship = true
        }

        if(applicationPayload.parentRelationship === 'FATHER' || applicationPayload.parentRelationship === 'OTHER') {
            // errors.relationship = true
            if(!applicantMother.name || applicantMother.name === '') {
                errors.motherName = true
            }

            if(!applicantMother.occupation || applicantMother.occupation === '') {
                errors.motherOccupation = true
            }
            
            if(!applicantMother.phone || applicantMother.phone === '') {
                errors.motherPhone = true
            }
            
            if(!applicantMother.homeAddress || applicantMother.homeAddress === '') {
                errors.motherHomeAddress = true
            }
            
            if(!applicantMother.workAddress || applicantMother.workAddress === '') {
                errors.motherWorkAddress = true
            }
        }

        if(applicationPayload.parentRelationship === 'MOTHER' || applicationPayload.parentRelationship === 'OTHER') {
            // errors.relationship = true
            if(!applicantFather.name || applicantFather.name === '') {
                errors.fathersName = true
            }

            if(!applicantFather.occupation || applicantFather.occupation === '') {
                errors.fatherOccupation = true
            }
            
            if(!applicantFather.phone || applicantFather.phone === '') {
                errors.fatherPhone = true
            }
            
            if(!applicantFather.homeAddress || applicantFather.homeAddress === '') {
                errors.fatherHomeAddress = true
            }
            
            if(!applicantFather.workAddress || applicantFather.workAddress === '') {
                errors.fatherWorkAddress = true
            }
        }

        if(hasVcmSiblings) {
            vcmSiblings.forEach((sibling, siblingIndex) => {
                if(!sibling.name || sibling.name === ''){
                    errors[`sibling-${siblingIndex}-name`] = true
                }
                if(!sibling.class || sibling.class === ''){
                    errors[`sibling-${siblingIndex}-class`] = true
                }
            })
        }

        // (if)

        setValidationErrors(errors)

        return errors
    }
    
    const completeStepTwo = async () => {
        try {
            if (Object.values(validateStep2Form()).includes(true)) {
    
                dispatch({
                    type: ERROR,
                    error: {response: {data: {
                        message: 'Please check the highlighted fields'
                    }}}
                })
                return
            }

            const payload = {
                mothersMaidenName: applicationPayload.mothersMaidenName,
                numberOfSiblings: applicationPayload.numberOfSiblings || 0,
                vcmSiblings: vcmSiblings,
                parentRelationship: applicationPayload.parentRelationship
            }

            if(applicationPayload.parentRelationship === 'FATHER' || applicationPayload.parentRelationship === 'OTHER') {
                payload.applicantMother = applicantMother
            }

            if(applicationPayload.parentRelationship === 'MOTHER' || applicationPayload.parentRelationship === 'OTHER') {
                payload.applicantFather = applicantFather
            }

            dispatch(updateApplication(applicationId, payload))

        } catch (error) {
            console.log(error)
            dispatch({
                type: ERROR,
                error
            })
            setLoading(false)
        }
        
    }

    const [hobbies, setHobbies] = useState(['']);

    const addHobby = () => {
        let tempHobbies = [...hobbies]
        tempHobbies.push('')
        setHobbies(tempHobbies)
    }

    const updateHobby = (index, value) => {
        let tempHobbies = [...hobbies]
        tempHobbies[index] = value
        setHobbies(tempHobbies)
    }

    const removeHobby = (index) => {
        let tempHobbies = [...hobbies]
        setHobbies(tempHobbies.splice(index, 1))
    }
    const [languages, setLanguages] = useState(['']);
    const addLanguage = () => {
        let tempLanguages = [...languages]
        tempLanguages.push('')
        setLanguages(tempLanguages)
    }

    const updateLanguage = (index, value) => {
        let tempLanguages = [...languages]
        tempLanguages[index] = value
        setLanguages(tempLanguages)
    }

    const removeLanguage = (index) => {
        let tempLanguages = [...languages]
        setLanguages(tempLanguages.splice(index, 1))
    }

    const sportsInterestsOptions = [
        {label:'Chess', value: 'chess'},
        {label:'Scrabble', value: 'scrabble'},
        {label:'Table tennis', value: 'table tennis'},
        {label:'Tennis', value: 'tennis'},
        {label:'Badminton', value: 'badminton'},
        {label:'Basketball', value: 'basketball'},
        {label:'Football', value: 'football'},
        {label:'Volleyball', value: 'volleyball'},
        {label:'Handball', value: 'handball'},
        {label:'Squash', value: 'squash'},
        {label:'Athletics', value: 'athletics'},
        {label:'Others', value: 'other'}
    ]

    const [sportsInterests, setSportsInterests] = useState(['']);
    const addSportsInterest = () => {
        let tempSportsInterests = [...sportsInterests]
        tempSportsInterests.push('')
        setSportsInterests(tempSportsInterests)
    }

    const updateSportInterest = (index, value) => {
        let tempSportsInterests = [...sportsInterests]
        tempSportsInterests[index] = value
        setSportsInterests(tempSportsInterests)
    }

    const removeSportsInterest = (index) => {
        let tempSportsInterests = [...sportsInterests]
        setSportsInterests(tempSportsInterests.splice(index, 1))
    }

    const validateStep3Form = () => {
        let errors = {}
        
        // if(!languages || (languages.length === 1 && languages[0] === '')) {
        //     errors.languages = true
        //     // setValidationErrors(errors)
        //     // return errors
        // } else {
        //     languages.forEach((language, languageIndex)=>{
        //         if(language === ''){
        //             errors[`languages-${languageIndex}`] = true
        //         }
        //     })
        // }

        console.log(errors)
        setValidationErrors(errors)

        return errors
    }

    const completeStepThree = async () => {
        try {
            if (Object.values(validateStep3Form()).includes(true)) {
    
                dispatch({
                    type: ERROR,
                    error: {response: {data: {
                        message: 'Please check the highlighted fields'
                    }}}
                })
                return
            }

            dispatch(updateApplication(applicationId, {
                // languages: languages.filter(language => {return language !== ''}),
                hobbies: hobbies.filter(hobby => {return hobby !== ''}),
                sportsInterests: sportsInterests.filter(interest => {return interest !== ''})
            }))

        } catch (error) {
            console.log(error)
            dispatch({
                type: ERROR,
                error
            })
            setLoading(false)
        }
    }

    const school = {
        school: '',
        fromYear: '',
        toYear: '',
        current: false,
        latestClass: '',
        type: ''
    }

    const [schools, setSchools] = useState([school]);

    const addSchool = () => {
        let tempSchools = [...schools]
        tempSchools.push(school)
        setSchools(tempSchools)
    }

    const updateSchool = (index,field, value) => {
        let tempSchools = [...schools]
        tempSchools[index][field] = value
        setSchools(tempSchools)
    }

    const removeSchool = (index) => {
        let tempSchools = [...schools]

        tempSchools.splice(index, 1)
        setSchools(tempSchools)
    }

    const validateStep4Form = () => {
        let errors = {}        
        
        schools.forEach((school, schoolIndex)=>{
            if(!school.school || school.school === ''){
                errors[`school-name-${schoolIndex}`] = true
            }
            if(!school.fromYear || school.fromYear === ''){
                errors[`school-from-${schoolIndex}`] = true
            }
            if(!school.current && (!school.toYear || school.toYear === '')){
                errors[`school-to-${schoolIndex}`] = true
            }
            if(!school.latestClass || school.latestClass === ''){
                errors[`school-class-${schoolIndex}`] = true
            }
            if(!school.type || school.type === ''){
                errors[`school-type-${schoolIndex}`] = true
            }
        })

        setValidationErrors(errors)

        return errors
    }

    const completeStepFour = async () => {
        try {
            if (Object.values(validateStep4Form()).includes(true)) {
    
                dispatch({
                    type: ERROR,
                    error: {response: {data: {
                        message: 'Please check the highlighted fields'
                    }}}
                })
                return
            }

            dispatch(updateApplication(applicationId, {
                schoolsAttended: schools
            }))

        } catch (error) {
            console.log(error)
            dispatch({
                type: ERROR,
                error
            })
            setLoading(false)
        }
        
    }

    const [files, setFiles] = useState([]);
    const [passportFile, setPassportFile] = useState(null);

	const addFile = (doc, file) => {
		let tempFiles = [...files]
		tempFiles.push({
			document: doc,
			file: file
		})
		setFiles(tempFiles)
	}

    const handleUpload = async (file) => {
        var formData = new FormData()
        formData.append('file', file.file )
        const headers = new Headers();
        headers.append("Authorization",  authHeader().Authorization);

        try {
    
            const doUpload = await fetch(`${process.env.REACT_APP_BASE_URL}/files/new`, {
                method: "POST",
                headers,
                body: formData,
            });
            const response = await doUpload.json();
    
           return response
        } catch (error) {
            dispatch({
                type: ERROR,
                error,
            });
        }
    }

    const [preferredCenter, setPreferredCenter] = useState(null);
    const [processing, setProcessing] = useState(false);

    const validateDocuments = () => {
        let errors = {}  
        
        if((!passportFile || passportFile === '') && !applicationPayload.passportPhotograph) {
            errors.passport = true
        }
        
        admission.requiredDocuments.forEach((doc, docIndex)=>{
            const existingFileIndex = files.findIndex(file => file.document.name === doc.name)
            if(doc.required && doc.required === true && !existingDocument(doc.name) && existingFileIndex < 0) {
                errors[`${docIndex}-doc`] = true
            }
        })

        setValidationErrors(errors)

        return errors
    }


    const completeStepFive = async () => {

        if (Object.values(validateDocuments()).includes(true)) {
    
            dispatch({
                type: ERROR,
                error: {response: {data: {
                    message: 'Please check the highlighted fields'
                }}}
            })
            return
        }

		try {
			// upload files
			setProcessing(true)
            let passportPhoto = null
            if(passportFile && passportFile !== null ) {
                passportPhoto = await handleUpload(passportFile)
            }
	
			let uploadedDocuments = []
			for (let index = 0; index < files.length; index++) {
				const element = files[index];
				const uploaded = await handleUpload(element.file)
	
				uploadedDocuments.push({
					name: element.document.name,
					documentSlug: element.document.slug,
					url: uploaded.data.file
				})
			}

			const payload = {
                documents: []
            }

            if (applicationPayload.documents && applicationPayload.documents.length > 0) {
                // replace the uploaded documents with new ones if available
                payload.documents = applicationPayload.documents
                uploadedDocuments.forEach(doc => {
                    const indexToReplace = payload.documents.findIndex(existingDoc => existingDoc.name === doc.name)
                    if(indexToReplace > -1) {
                        payload.documents[indexToReplace] = doc
                    } else {
                        payload.documents.push(doc)
                    }
                })
            } else  {
                payload.documents = uploadedDocuments
            }
            
            if (passportPhoto !== null) {
                payload.passportPhotograph = passportPhoto.data.file
            }

            dispatch(updateApplication(applicationId, payload))
            setProcessing(false)
            setFiles([])
            setChangingDocument(null)
			
            } catch (error) {
            setFiles([])
            setChangingDocument(null)
            console.log('error -> ', error)
			setProcessing(false)

			dispatch({
				type: ERROR,
				error: error
			})
		}
	}

    // const [attested, setAttested] = useState(false);
    // const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [applicationCompleted, setApplicationCompleted] = useState(false);

    const validateDeclaration = () => {
        let errors = {}  
        
        declarations.forEach((declaration, declarationIndex)=>{
            if(declaration.selected === false) {
                errors[`declaration-${declarationIndex}`] = true
            }
        })

        if(!preferredCenter || preferredCenter === null) {
            errors['preferredCenter'] = true
        }

        setValidationErrors(errors)

        return errors
    }

    const [additionalInformation, setAdditionalInformation] = useState(null);
    const [supportingDocument, setSupportingDocument] = useState(null);

    const submitApplication = async () => {
		try {
            
            if (Object.values(validateDeclaration()).includes(true)) {
    
                dispatch({
                    type: ERROR,
                    error: {response: {data: {
                        message: 'Please accept all declarations'
                    }}}
                })
                return
            }

            let supportingDoc = null

            if(supportingDocument && supportingDocument !== null ) {
                supportingDoc = await handleUpload(supportingDocument)
            }

			const payload = {
                completed: true,
                preferredCenter: preferredCenter.value,
                additionalInformation: additionalInformation,
			}

            if(supportingDoc !== null && supportingDoc?.data?.file) {
                payload.supportingDocuments = supportingDoc.data.file

            }

            setApplicationCompleted(true)
            dispatch(updateApplication(applicationId, payload))
	
			
		} catch (error) {
            console.log(error)
			dispatch({
				type: ERROR,
				error: error
			})
		}
	}

    const religions = [
        {
            label: 'Christianity',
            value: 'CHRISTIANITY'
        },
        {
            label: 'Islam',
            value: 'ISLAM'
        },
        {
            label: 'Other',
            value: 'OTHER'
        }
    ]

    const complexions = [
        {label: 'Light', value: 'LIGHT'},
        {label: 'Brown', value: 'BROWN'},
        {label: 'Dark', value: 'DARK'}
    ]

    const genders = [
        {
            label: 'Female',
            value: 'FEMALE'
        },
        {
            label: 'Male',
            value: 'MALE'
        }
    ]

    const relationships = [
        {
            label: 'Father',
            value: 'FATHER'
        },
        {
            label: 'Mother',
            value: 'MOTHER'
        },
        {
            label: 'Guardian/Other',
            value: 'OTHER'
        }
    ]
// 'BASIC7', 'BASIC8', 'BASIC9'
    const siblingClassOptions = [
        {label: 'Basic 7', value: 'BASIC7'},
        {label: 'Basic 8', value: 'BASIC8'},
        {label: 'Basic 9', value: 'BASIC9'},
        {label: 'SSS1', value: 'SSS1'},
        {label: 'SSS2', value: 'SSS2'},
        {label: 'SSS3', value: 'SSS3'},
    ]
    
    const schoolTypes = [
        {
            label: 'Primary School',
            value: 'PRIMARY'
        },
        {
            label: 'Secondary School',
            value: 'SECONDARY'
        }
    ]

    const existingDocument = (docName) => {
        return applicationPayload.documents.find(document => docName === document.name)
    }

    const docType = (url) => {
        return url.split('.')[url.split('.').length - 1]
    }
        
    const docName = (url) => {
        return url.split('/')[url.split('/').length - 1]
    }

    const declarationOptions = [
        {
            label: '1',
            description: 'Apply for a place at VAATIA COLLEGE MAKURDI for the applicant to become a student at the College with effect from the admission date specified above', 
            selected: false
        },
        {
            label: '2',
            description: 'Accept that this application is subject to the college’s admissions policy current as at the date of receipt of this application by the College as such policy is from time to time revised or amended.', 
            selected: false
        },
        {
            label: '3',
            description: 'Acknowledge that all applicants are required to undertake an entrance assessment.', 
            selected: false
        },
        {
            label: '4',
            description: 'Recognize that there is no obligation on the College to offer a place at the College for the applicant or to accept the applicant as a student of the College;', 
            selected: false
        },
        {
            label: '5',
            description: 'Authorize the applicant’s current school named above to disclose to VAATIA COLLEGE MAKURDI information on the applicant (e.g. academic report and student profile) and where appropriate', 
            selected: false
        },
        {
            label: '6',
            description: 'Authorize the applicant’s current school named above to confirm to the College whether all fees in respect of the student have been paid to that current school, and authorize the College to disclose this application and authorization to that current school.', 
            selected: false
        },
        {
            label: '7',
            description: 'Agree to abide by the Rules and Regulations of the College which are subject to amendment by the College.', 
            selected: false
        },
        // {
        //     label: '8',
        //     description: 'Accept the College has the right to administer sanctions if the College Rules and Regulations', 
        //     selected: false
        // },
    ]

    const [declarations, setDeclarations] = useState(declarationOptions);

    const toggleDeclaration = (index) => {
        let tempDeclarations = [...declarations]
        tempDeclarations[index].selected = !tempDeclarations[index].selected
        setDeclarations(tempDeclarations)
    } 

    const [changingDocument, setChangingDocument] = useState(null);
    return (
        <UserLayout>
            {loading 
            ? 
                <Loader />
            :
                <div className='w-full mx-auto xl:flex items-start gap-x-8'>
                    <div className='hidden xl:block w-full xl:w-3/12'>

                        {applicationSteps.map((step, stepIndex)=>(
                            <div onClick={()=>{
                                    if(completedSteps.includes(stepIndex) || completedSteps.includes(stepIndex - 1)){
                                        setActiveStep(stepIndex)
                                    }
                                }} 
                                key={stepIndex} 
                                className={`p-5 my-4 cursor-pointer border border-transparent transition duration-200 hover:border-gray-200 hover:shadow-lg relative ${activeStep === stepIndex && 'border-r-4 border-vcm-purple border-l-transparent border-t-transparent border-b-transparent'}`}
                            >
                                {completedSteps.includes(stepIndex) && <span className=' absolute top-[10px] right-[10px] rounded-full flex items-center justify-center w-[20px] h-[20px] border border-vcm-purple'>
                                    <CheckIcon className='w-[12px] h-[12px] text-vcm-purple' />
                                </span>}
                                <p className='uppercase tracking-[0.2em] text-sm text-vcm-purple'>{step.label}
                                </p>
                                <p className='text-gray-600 text-xs'>{step.description}</p>
                            </div>
                        ))}

                    </div>

                    {activeStep < applicationSteps.length - 1 && <div className='w-full p-[16px] lg:hidden rounded-[8px] border border-vcm-purple bg-vcm-light-purple bg-opacity-60 relative'>

                        {completedSteps.includes(activeStep) && <span className='absolute top-[10px] rounded right-[10px] flex items-center justify-center px-2 py-1 text-[10px] bg-vcm-purple text-white bg-opacity-80'>
                            completed
                        </span>}
                        <p className='uppercase tracking-[0.2em] text-[12px] text-vcm-purple'>current step</p>

                        <h3 className='font-[500] text-[15px] text-vcm-purple'>{applicationSteps[activeStep].label}
                        </h3>
                        <p className='text-gray-600 text-sm'>{applicationSteps[activeStep].description}</p>
                    </div>}

                    {activeStep > 0 && <button onClick={()=>{setActiveStep(activeStep - 1)}} className='lg:hidden flex items-center gap-x-2 text-gray-500 hover:text-vcm-purple transition duration-200 mt-4 text-sm'>
                        <ArrowIcon className={`w-4 h-4 rotate-180`} />
                        Previous step
                    </button>}

                    <div className={`w-full xl:w-6/12 pb-5 xl:p-5 mt-12 xl:mt-0`}>
                        {activeStep === 0 && 
                            <div className='w-full lg:w-11/12 mx-auto'>
                                <div>
                                    <p className="text-sm text-primary mb-1 tracking-[0.2em] uppercase">step {activeStep + 1} of {applicationSteps.length}: <span className="tracking-normal text-sm">{applicationSteps[activeStep].label}</span></p>

                                    <div className='mt-4'>
                                        <RadioGroup 
                                            items={entryClasses} 
                                            inputLabel={'Which class are you applying to?'}
                                            preSelectedIndex={entryClasses.findIndex(item => item.value === applicationPayload.class)}
                                            requiredField={true}
                                            returnSelected={(value) => {setApplicationPayload({...applicationPayload, ...{class: value.value}})}} 
                                            hasError={validationErrors && validationErrors.class}  
                                            titleField='label'
                                        />
                                    </div>

                                    <div className='mt-4'>
                                        <TextField
                                            inputLabel="Applicant's surname" 
                                            fieldId="last-name" 
                                            requiredField={true}
                                            inputType="text" 
                                            preloadValue={applicationPayload.lastName || ''}
                                            hasError={validationErrors && validationErrors.lastName} 
                                            returnFieldValue={(value)=>{setApplicationPayload({...applicationPayload, ...{lastName: value}})}}
                                        />
                                    </div>

                                    <div className='mt-4'>
                                        <TextField
                                            inputLabel="Applicant's first name" 
                                            fieldId="first-name" 
                                            requiredField={true}
                                            inputType="text" 
                                            preloadValue={applicationPayload.firstName || ''}
                                            hasError={validationErrors && validationErrors.firstName} 
                                            returnFieldValue={(value)=>{setApplicationPayload({...applicationPayload, ...{firstName: value}})}}
                                        />
                                    </div>
                                    
                                    <div className='mt-4'>
                                        <TextField
                                            inputLabel="Other names" 
                                            fieldId="email" 
                                            inputType="text" 
                                            preloadValue={applicationPayload.otherNames || ''}
                                            hasError={validationErrors && validationErrors.email} 
                                            returnFieldValue={(value)=>{setApplicationPayload({...applicationPayload, ...{otherNames: value}})}}
                                        />
                                    </div>
                                    <div className='mt-4 w-full'>

                                        <SelectField
                                            selectOptions={complexions}
                                            inputLabel="Applicant's Complexion"
                                            titleField="label"
                                            displayImage={false}
                                            imageField=""
                                            // preSelectedIndex={complexions.findIndex(item => item.value === applicationPayload.complexion)}
                                            preSelectedIndex={complexions.findIndex(item => item.value === applicationPayload.complexion)}
                                            // preSelectedLabel={`value`}
                                            fieldId="applicant-complexion"
                                            hasError={validationErrors && validationErrors.complexion}
                                            // return id of accounts of the selected option
                                            returnFieldValue={(value) => {setApplicationPayload({...applicationPayload, ...{complexion: value.value}})}}
                                        />
                                    </div>
                                    <div className='mt-4'>
                                        <RadioGroup 
                                            items={genders} 
                                            preSelectedIndex={genders.findIndex(gender => gender.value === applicationPayload.gender)}
                                            inline={true}
                                            inputLabel={'Gender'}
                                            requiredField={true}
                                            returnSelected={(value) => {setApplicationPayload({...applicationPayload, ...{gender: value.value}})}} 
                                            hasError={validationErrors && validationErrors.gender}  
                                            titleField='label'
                                        />
                                    </div>
                                    <div className='mt-4'>
                                        <DateField
                                            inputLabel="Date of Birth" 
                                            fieldId="dob" 
                                            requiredField={true}
                                            inputType="text" 
                                            preloadValue={moment(applicationPayload.dob).format('YYYY-MM-DD') || ''}
                                            hasError={validationErrors && validationErrors.dob} 
                                            returnFieldValue={(value)=>{
                                                const formattedDate = new Date(value)
                                                setApplicationPayload({...applicationPayload, ...{dob: formattedDate}})}}
                                        />
                                    </div>

                                    <div className='mt-4'>
                                        <TextField
                                            inputLabel="Applicant's place of birth" 
                                            fieldId="first-name" 
                                            requiredField={true}
                                            inputType="text" 
                                            preloadValue={applicationPayload.placeOfBirth || ''}
                                            hasError={validationErrors && validationErrors.placeOfBirth} 
                                            returnFieldValue={(value)=>{setApplicationPayload({...applicationPayload, ...{placeOfBirth: value}})}}
                                        />
                                    </div>

                                    <div className='mt-4'>
                                        <TextField
                                            inputLabel="Tribe" 
                                            fieldId="first-name" 
                                            requiredField={true}
                                            inputType="text" 
                                            preloadValue={applicationPayload.tribe || ''}
                                            hasError={validationErrors && validationErrors.tribe} 
                                            returnFieldValue={(value)=>{setApplicationPayload({...applicationPayload, ...{tribe: value}})}}
                                        />
                                    </div>

                                    <div className='mt-4'>
                                        <RadioGroup 
                                            items={religions} 
                                            preSelectedIndex={religions.findIndex(religion => religion.value === applicationPayload.religion)}
                                            inputLabel={'Religion'}
                                            inline={true}
                                            requiredField={true}
                                            returnSelected={(value) => {setApplicationPayload({...applicationPayload, ...{religion: value.value}})}} 
                                            hasError={validationErrors && validationErrors.religion}  
                                            titleField='label'
                                        />
                                    </div>

                                    <div className='w-full mt-4'>
                                        <AutocompleteSelect
                                            selectOptions = {Nationalities}
                                            requiredField={true}
                                            inputLabel = "Nationality"
                                            titleField = "name"
                                            displayImage = {true}
                                            imageField = "image"
                                            fieldId = "citizenship"
                                            preSelected={applicationPayload.nationality && applicationPayload.nationality !== '' ? applicationPayload.nationality : ''}
                                            preSelectedLabel='name'
                                            hasError = {validationErrors && validationErrors.nationality}
                                            returnFieldValue={(value)=>{setApplicationPayload({...applicationPayload, ...{
                                                countryCode: value.code,
                                                nationality: value.name
                                            }})}}
                                            disabled={false}
                                        />
                                    </div>

                                    <div className='w-full mt-4'>
                                        {applicationPayload.countryCode === 'NG' || applicationPayload.nationality === 'Nigeria' ?
                                            <AutocompleteSelect
                                                selectOptions = {parseNigerianStates()}
                                                inputLabel = "State of Origin"
                                                requiredField={true}
                                                titleField = "label"
                                                displayImage = {false}
                                                imageField = ""
                                                fieldId = "State"
                                                preSelected={applicationPayload.state && applicationPayload.state !== '' ? applicationPayload.state : ''}
                                                preSelectedLabel='label'
                                                hasError = {validationErrors && validationErrors.state}
                                                returnFieldValue={(value)=>{selectState(value)}}
                                            />
                                        :
                                            <TextField 
                                                inputType="text" 
                                                fieldId="state"
                                                requiredField={true}
                                                inputLabel="State of Origin" 
                                                hasError={validationErrors && validationErrors.state === true} 
                                                preloadValue={applicationPayload.state || ''}
                                                // disabled={true}
                                                disabled={false}
                                                returnFieldValue={(value)=>{setApplicationPayload({...applicationPayload, ...{state: value}})}}
                                            />

                                        }
                                    </div>

                                    <div className='w-full mt-4'>
                                        {(applicationPayload.countryCode === 'NG' || applicationPayload.nationality === 'Nigeria') && lgasEnabled === true ?
                                            <AutocompleteSelect
                                                selectOptions = {activeLgas}
                                                inputLabel = "LGA/City"
                                                titleField = ""
                                                displayImage = {false}
                                                imageField = ""
                                                disabled={!lgasEnabled}
                                                fieldId = "userLga"
                                                preSelected={applicationPayload?.lga && applicationPayload?.lga !== '' ? applicationPayload?.lga : ''}
                                                requiredField={true}
                                                preSelectedLabel=''
                                                hasError = {validationErrors && validationErrors?.lga}
                                                returnFieldValue={(value)=>{
                                                    console.log('selected value:: ', value)
                                                    setApplicationPayload({...applicationPayload, ...{ lga: value }})}}
                                            />
                                        :
                                            <TextField 
                                                inputType="text" 
                                                fieldId="city"
                                                inputLabel="LGA/City" 
                                                hasError={validationErrors && validationErrors?.lga === true} 
                                                preloadValue={applicationPayload?.lga || ''}
                                                // disabled={true}
                                                requiredField={true}
                                                disabled={false}
                                                returnFieldValue={(value)=>{setApplicationPayload({...applicationPayload, ...{ lga: value }})}}
                                            />
                                        }
                                    </div>

                                    <p className='text-sm mb-1 mt-5'>Applicant's spoken languages</p>
                                    <p className='text-sm text-gray-500'>Please provide spoken languages below. Add a language and click on the "add language" button to add more</p>
                                    {validationErrors.languages && <p className='text-xs text-red-600 mb-2 mt-3'>Please add at least one language</p>}
                                    {languages.map((language, languageIndex) => (<div key={languageIndex} className='mt-4 flex items-end justify-between gap-x-2'>
                                        <div className='w-full'>
                                            <TextField
                                                inputLabel="Language" 
                                                fieldId={`language-${languageIndex}`}
                                                requiredField={languageIndex === 0 ? true : false}
                                                inputType="text" 
                                                preloadValue={language}
                                                hasError={validationErrors && validationErrors[`languages-${languageIndex}`]} 
                                                returnFieldValue={(value)=>{updateLanguage(languageIndex, value)}}
                                            />
                                        </div>
                                        {languageIndex > 0 && <div className='pb-[10px]'>
                                            <button onClick={()=>{removeLanguage(languageIndex)}} className='text-gray-500 transition duration-200 hover:text-red-600'><TrashIcon className={`w-5 h-5`} /></button>
                                        </div>}
                                    </div>))}
                                    <div className='flex flex-row-reverse w-full mt-3'>
                                        <button onClick={()=>{addLanguage()}} className='text-gray-300 bg-vcm-dark-gray p-2 rounded transition duration-200 hover:bg-vcm-light-purple hover:text-vcm-dark-gray text-sm flex items-center gap-x-2'>
                                        <PlusIcon className={`w-5 h-5`} /> Add another language
                                        </button>
                                    </div>
                                    
                                    <div className='mt-8 flex items-center justify-between'>
                                        <button onClick={()=>{setActiveStep(1)}} className='flex items-center gap-x-2 text-gray-500 hover:text-vcm-purple transition duration-200 mb-3 text-sm'>
                                            <ArrowIcon className={`w-4 h-4 rotate-180`} />
                                            Previous step
                                        </button>
                                        <div className='w-max'>
                                            <FormButton buttonLabel={
                                                <span className='flex items-center justify-center gap-x-4'>
                                                    Save & Continue <ArrowIcon className={`w-5 h-5`}/>
                                                </span>
                                            } buttonAction={()=>{completeStepOne()}} processing={applicationsSelector.creatingApplication} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        }

                        {activeStep === 1 && 
                            <div className='w-full lg:w-11/12 mx-auto'>
                                <div>
                                    <p className="text-sm text-primary mb-1 tracking-[0.2em] uppercase">step {activeStep + 1} of {applicationSteps.length}: <span className="tracking-normal text-sm">{applicationSteps[activeStep].label}</span></p>

                                    <div className='pb-4 border-b border-gray-300'>
                                        <p 
                                            className={`text- lg:text-md cursor-text z-10 relative py-1 transition mb-2 block duration-200  
                                           text-gray-800`}
                                        >
                                        How are you related to this applicant?
                                        </p>

                                        <RadioGroup 
                                            items={relationships} 
                                            preSelectedIndex={relationships.findIndex(rel => rel.value === applicationPayload.parentRelationship)}
                                            inline={false}
                                            inputLabel={'Relationship'}
                                            requiredField={true}
                                            returnSelected={(value) => {setApplicationPayload({...applicationPayload, ...{parentRelationship: value.value}})}} 
                                            hasError={validationErrors && validationErrors.relationship}  
                                            titleField='label'
                                        />
                                    </div>

                                    {applicationPayload.parentRelationship && applicationPayload.parentRelationship !== '' && <div>
                                        <p className='text-gray-600 text-sm mt-5'>
                                            We already have your information saved as the <span className='font-[600] text-gray-900'></span>{applicationPayload.parentRelationship.toLowerCase()} of the applicant, please provide the applicant's 
                                            {applicationPayload.parentRelationship === 'FATHER' && ' mother'}{applicationPayload.relationship === 'MOTHER' && ' father'}{applicationPayload.parentRelationship === 'OTHER' && 'parent'}'{applicationPayload.parentRelationship === 'OTHER' &&<span></span>}s details below.
                                        </p>
                                    </div>}

                                    {(applicationPayload.parentRelationship === 'MOTHER' || applicationPayload.parentRelationship === 'OTHER') && <>
                                        <p className='text-gray-600 text-sm mt-12 font-[600]'>
                                            Applicant's Father's Details.
                                        </p>
                                        <div className='mt-4'>
                                            <TextField
                                                inputLabel="Father's name" 
                                                fieldId="father-name" 
                                                requiredField={true}
                                                inputType="text" 
                                                preloadValue={applicantFather.name || ''}
                                                hasError={validationErrors && validationErrors.fathersName} 
                                                returnFieldValue={(value)=>{setApplicantFather({...applicantFather, ...{name: value}})}}
                                            />
                                        </div>

                                        <div className='mt-4'>
                                            <TextField
                                                inputLabel="Father's phone number" 
                                                fieldId="father-phone" 
                                                requiredField={true}
                                                inputType="text" 
                                                preloadValue={applicantFather.phone || ''}
                                                hasError={validationErrors && validationErrors.fatherPhone} 
                                                returnFieldValue={(value)=>{setApplicantFather({...applicantFather, ...{phone: value}})}}
                                            />

                                            <label 
                                                className={`text-sm lg:text-md cursor-text z-10 relative py-1 transition mt-2 block duration-200 text-gray-500`}
                                            >
                                                Preferably WhatsApp enabled
                                            </label>
                                        </div>

                                        <div className='mt-4'>
                                            <TextField
                                                inputLabel="Father's email address" 
                                                fieldId="father-email" 
                                                requiredField={false}
                                                inputType="text" 
                                                preloadValue={applicantFather.email || ''}
                                                hasError={false} 
                                                returnFieldValue={(value)=>{setApplicantFather({...applicantFather, ...{email: value}})}}
                                            />
                                        </div>

                                        <div className='mt-4'>
                                            <TextField
                                                inputLabel="Home address" 
                                                fieldId="father-home-address" 
                                                inputType="text"
                                                requiredField={true} 
                                                preloadValue={applicantFather.homeAddress || ''}
                                                hasError={validationErrors && validationErrors.fatherHomeAddress} 
                                                returnFieldValue={(value)=>{setApplicantFather({...applicantFather, ...{homeAddress: value}})}}
                                            />
                                        </div>

                                        <div className='mt-4'>
                                            <TextField
                                                inputLabel="Occupation" 
                                                fieldId="father-occupation" 
                                                inputType="text"
                                                requiredField={true} 
                                                preloadValue={applicantFather.occupation || ''}
                                                hasError={validationErrors && validationErrors.fatherOccupation} 
                                                returnFieldValue={(value)=>{setApplicantFather({...applicantFather, ...{occupation: value}})}}
                                            />
                                        </div>

                                        <div className='mt-4'>
                                            <TextField
                                                inputLabel="Work address" 
                                                fieldId="father-work-address" 
                                                inputType="text"
                                                requiredField={true} 
                                                preloadValue={applicantFather.workAddress || ''}
                                                hasError={validationErrors && validationErrors.fatherWorkAddress} 
                                                returnFieldValue={(value)=>{setApplicantFather({...applicantFather, ...{workAddress: value}})}}
                                            />
                                        </div>

                                    </>}

                                    {(applicationPayload.parentRelationship === 'FATHER' || applicationPayload.parentRelationship === 'OTHER') && <>
                                        <p className='text-gray-600 text-sm font-[600] mt-12'>
                                            Applicant's Mother's Details.
                                        </p>
                                        <div className='mt-4'>
                                            <TextField
                                                inputLabel="Mother's name" 
                                                fieldId="mother-name" 
                                                requiredField={true}
                                                inputType="text" 
                                                preloadValue={applicantMother.name || ''}
                                                hasError={validationErrors && validationErrors.motherName} 
                                                returnFieldValue={(value)=>{setApplicantMother({...applicantMother, ...{name: value}})}}
                                            />
                                        </div>

                                        <div className='mt-4'>
                                            <TextField
                                                inputLabel="Mother's phone number" 
                                                fieldId="father-phone" 
                                                requiredField={true}
                                                inputType="text" 
                                                preloadValue={applicantMother.phone || ''}
                                                hasError={validationErrors && validationErrors.motherPhone} 
                                                returnFieldValue={(value)=>{setApplicantMother({...applicantMother, ...{phone: value}})}}
                                            />
                                            <label 
                                                className={`text-sm lg:text-md cursor-text z-10 relative py-1 transition mt-2 block duration-200 text-gray-500`}
                                            >
                                                Preferably WhatsApp enabled
                                            </label>
                                        </div>

                                        <div className='mt-4'>
                                            <TextField
                                                inputLabel="Mother's email address" 
                                                fieldId="father-email" 
                                                requiredField={false}
                                                inputType="text" 
                                                preloadValue={applicantMother.email || ''}
                                                hasError={false} 
                                                returnFieldValue={(value)=>{setApplicantMother({...applicantMother, ...{email: value}})}}
                                            />
                                        </div>

                                        <div className='mt-4'>
                                            <TextField
                                                inputLabel="Home address" 
                                                fieldId="father-home-address" 
                                                inputType="text"
                                                requiredField={true} 
                                                preloadValue={applicantMother.homeAddress || ''}
                                                hasError={validationErrors && validationErrors.motherHomeAddress} 
                                                returnFieldValue={(value)=>{setApplicantMother({...applicantMother, ...{homeAddress: value}})}}
                                            />
                                        </div>

                                        <div className='mt-4'>
                                            <TextField
                                                inputLabel="Occupation" 
                                                fieldId="father-occupation" 
                                                inputType="text"
                                                requiredField={true} 
                                                preloadValue={applicantMother.occupation || ''}
                                                hasError={validationErrors && validationErrors.motherOccupation} 
                                                returnFieldValue={(value)=>{setApplicantMother({...applicantMother, ...{occupation: value}})}}
                                            />
                                        </div>

                                        <div className='mt-4'>
                                            <TextField
                                                inputLabel="Work address" 
                                                fieldId="father-work-address" 
                                                inputType="text"
                                                requiredField={true} 
                                                preloadValue={applicantMother.workAddress || ''}
                                                hasError={validationErrors && validationErrors.motherWorkAddress} 
                                                returnFieldValue={(value)=>{setApplicantMother({...applicantMother, ...{workAddress: value}})}}
                                            />
                                        </div>
                                        
                                    </>}

                                    <div className='mt-12'>
                                        <TextField
                                            inputLabel="Mother's maiden name" 
                                            fieldId="mm-name" 
                                            requiredField={true}
                                            inputType="text" 
                                            preloadValue={applicationPayload.mothersMaidenName || ''}
                                            hasError={validationErrors && validationErrors.mothersMaidenName} 
                                            returnFieldValue={(value)=>{setApplicationPayload({...applicationPayload, ...{mothersMaidenName: value}})}}
                                        />
                                        <label className='text-sm text-gray-500 mt-1 block '>Applicant's mother's maiden name</label>
                                    </div>

                                    <div className='my-5 border-t border-gray-300' />

                                    <p className='text-gray-600 text-sm font-[600] mt-12'>
                                        Applicant's Siblings.
                                    </p>
                                    <div className='mt-4'>
                                        <NumberField 
                                            inputType="number" 
                                            fieldId={`number-siblings`}
                                            inputLabel="Number of siblings" 
                                            requiredField={false}
                                            preloadValue={applicationPayload.numberOfSiblings || ''}
                                            hasError={false} 
                                            returnFieldValue={(value)=>{setApplicationPayload({...applicationPayload, ...{numberOfSiblings: value}})}}
                                        />
                                    </div> 

                                    <div className='w-full my-4'>
                                        <Checkbox
                                            CheckboxLabel="Check this box if the applicant has any siblings currently schooling at Vaatia College Makurdi."
                                            checkboxToggleFunction={()=>{
                                                setHasVcmSiblings(!hasVcmSiblings)
                                            }} 
                                            isChecked={hasVcmSiblings} 
                                            hasError={false} 
                                        />
                                    </div>

                                    {hasVcmSiblings && 
                                        <>
                                            <label className='text-sm text-gray-500 mt-1 block mb-2'>Please provide details of applicant's siblings below. You can click the "add another sibling button if you need to add more."</label>
                                            
                                            <div className='w-full my-4 p-4 border rounded'>
                                            {vcmSiblings.map((sibling, siblingIndex) => (
                                                <div key={siblingIndex} className='w-full relative mb-6'>
                                                    {siblingIndex > 0 && <div className='absolute top-0 right-0 z-50'>
                                                        <button onClick={()=>{removeVcmSibling(siblingIndex)}} className='text-gray-500 transition duration-200 hover:text-red-600'><TrashIcon className={`w-5 h-5`} /></button>
                                                    </div>}
                                                    <div className='mt-4'>
                                                        <TextField
                                                            inputLabel="Sibling name" 
                                                            fieldId="sibling-name" 
                                                            requiredField={true}
                                                            inputType="text" 
                                                            preloadValue={sibling.name || ''}
                                                            hasError={validationErrors && validationErrors[`sibling-${siblingIndex}-name`]} 
                                                            returnFieldValue={(value)=>{updateVcmSibling(siblingIndex, 'name', value)}}
                                                        />
                                                    </div>
                                                    <div className='mt-4'>
                                                        <SelectField
                                                            selectOptions={siblingClassOptions}
                                                            inputLabel="Sibling's current class"
                                                            titleField="label"
                                                            displayImage={false}
                                                            requiredField={true}
                                                            imageField=""
                                                            preSelectedIndex={siblingClassOptions.findIndex(classOptions => classOptions.value === sibling.class)}
                                                            fieldId={`sibling-${siblingIndex}-class`}
                                                            hasError={validationErrors && validationErrors[`sibling-${siblingIndex}-class`]}
                                                            returnFieldValue={(value) => {updateVcmSibling(siblingIndex, 'class', value.value)}}
                                                        />
                                                    </div>
                                                    <div className='mt-4'>
                                                        <TextField
                                                            inputLabel="Sibling's admission number" 
                                                            fieldId="sibling-student-name" 
                                                            requiredField={false}
                                                            inputPlaceholder={`eg: VCM-1234-2024`}
                                                            inputType="text" 
                                                            preloadValue={sibling.studentCode || ''}
                                                            hasError={validationErrors && validationErrors[`sibling-${siblingIndex}-student-code`]} 
                                                            returnFieldValue={(value)=>{updateVcmSibling(siblingIndex, 'studentCode', value)}}
                                                        />
                                                        <label className='text-sm text-gray-500 mt-1 block '>Please provide this sibling's admission number if you know it. </label>
                                                    </div>
                                                </div>
                                            ))}
                                            <div className='flex flex-row-reverse w-full mt-3'>
                                                    <button onClick={()=>{addVcmSibling()}} className='text-gray-300 bg-vcm-dark-gray p-2 rounded transition duration-200 hover:bg-vcm-light-purple hover:text-vcm-dark-gray text-sm flex items-center gap-x-2'>
                                                        <PlusIcon className={`w-5 h-5`} /> Add another sibling
                                                    </button>
                                                </div>

                                            </div>
                                        </>
                                    }
                                    
                                    <div className='mt-8 flex items-center justify-between'>
                                        <button onClick={()=>{setActiveStep(0)}} className='flex items-center gap-x-2 text-gray-500 hover:text-vcm-purple transition duration-200 mb-3 text-sm'>
                                            <ArrowIcon className={`w-4 h-4 rotate-180`} />
                                            Previous step
                                        </button>
                                        <div className='w-max'>
                                            <FormButton buttonLabel={
                                                <span className='flex items-center justify-center gap-x-4'>
                                                    Save & Continue <ArrowIcon className={`w-5 h-5`}/>
                                                </span>
                                            } buttonAction={()=>{completeStepTwo()}} processing={applicationsSelector.updatingApplication} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        }

                        {activeStep === 2 && 
                            <div className='w-full lg:w-11/12 mx-auto'>
                                <div>
                                    <p className="text-sm text-primary mb-1 tracking-[0.2em] uppercase">step {activeStep + 1} of {applicationSteps.length}: <span className="tracking-normal text-sm">{applicationSteps[activeStep].label}</span></p>

                                    <p className='text-sm mb-1 mt-5'>Applicant's Sports Interests</p>
                                    <p className='text-sm text-gray-500'>Please provide applicant sports interests below. Add a sport and click on the "add sport" button to add more</p>

                                    {sportsInterests.map((interest, interestIndex) => (<div key={interestIndex} className='mt-4 flex items-end justify-between gap-x-2'>
                                        <div className='w-full'>
                                            <SelectField
                                                selectOptions={sportsInterestsOptions}
                                                inputLabel="Sports/Vocational interest"
                                                titleField="label"
                                                displayImage={false}
                                                imageField=""
                                                preSelectedIndex={sportsInterestsOptions.findIndex(item => item.value === interest)}
                                                // preSelected={''}
                                                // preSelectedLabel={``}
                                                fieldId={`interest-${interestIndex}`}
                                                hasError={false}
                                                returnFieldValue={(value)=>{updateSportInterest(interestIndex, value.value)}}
                                            />
                                        </div>

                                        {sportsInterests[interestIndex] === 'other' && <div className='w-full'>
                                            <TextField
                                                inputLabel="Please specify" 
                                                fieldId={`other-interest-${interestIndex}`} 
                                                requiredField={false}
                                                inputType="text" 
                                                preloadValue={interest}
                                                hasError={false} 
                                                returnFieldValue={(value)=>{updateSportInterest(interestIndex, value)}}
                                            />
                                        </div>}

                                        {interestIndex > 0 && <div className='pb-[10px]'>
                                            <button onClick={()=>{removeSportsInterest(interestIndex)}} className='text-gray-500 transition duration-200 hover:text-red-600'><TrashIcon className={`w-5 h-5`} /></button>
                                        </div>}
                                    </div>))}

                                    <div className='flex flex-row-reverse w-full mt-3'>
                                        <button onClick={()=>{addSportsInterest()}} className='text-gray-300 bg-vcm-dark-gray p-2 rounded transition duration-200 hover:bg-vcm-light-purple hover:text-vcm-dark-gray text-sm flex items-center gap-x-2'>
                                        <PlusIcon className={`w-5 h-5`} /> Add sport
                                        </button>
                                    </div>

                                    <p className='text-sm mb-1 mt-5'>Applicant's Hobbies</p>
                                    <p className='text-sm text-gray-500'>Please provide applicant hobbies below. Add a hobby and click on the "add hobby" button to add more</p>

                                    {hobbies.map((hobby, hobbyIndex) => (<div key={hobbyIndex} className='mt-4 flex items-end justify-between gap-x-2'>
                                        <div className='w-full'>
                                            <TextField
                                                inputLabel="Hobby" 
                                                fieldId={`hobby-${hobbyIndex}`} 
                                                requiredField={false}
                                                inputType="text" 
                                                preloadValue={hobby}
                                                hasError={false} 
                                                returnFieldValue={(value)=>{updateHobby(hobbyIndex, value)}}
                                            />
                                        </div>
                                        {hobbyIndex > 0 && <div className='pb-[10px]'>
                                            <button onClick={()=>{removeHobby(hobbyIndex)}} className='text-gray-500 transition duration-200 hover:text-red-600'><TrashIcon className={`w-5 h-5`} /></button>
                                        </div>}
                                    </div>))}

                                    <div className='flex flex-row-reverse w-full mt-3'>
                                        <button onClick={()=>{addHobby()}} className='text-gray-300 bg-vcm-dark-gray p-2 rounded transition duration-200 hover:bg-vcm-light-purple hover:text-vcm-dark-gray text-sm flex items-center gap-x-2'>
                                        <PlusIcon className={`w-5 h-5`} /> Add hobby
                                        </button>
                                    </div>
                                    
                                    <div className='mt-8 flex items-center justify-between'>
                                        <button onClick={()=>{setActiveStep(1)}} className='flex items-center gap-x-2 text-gray-500 hover:text-vcm-purple transition duration-200 mb-3 text-sm'>
                                            <ArrowIcon className={`w-4 h-4 rotate-180`} />
                                            Previous step
                                        </button>
                                        <div className='w-max'>
                                            <FormButton buttonLabel={
                                                <span className='flex items-center justify-center gap-x-4'>
                                                    Save & Continue <ArrowIcon className={`w-5 h-5`}/>
                                                </span>
                                            } buttonAction={()=>{completeStepThree()}} processing={applicationsSelector.updatingApplication} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        }

                        {activeStep === 3 && 
                            <div className='w-full lg:w-11/12 mx-auto'>
                                <div>
                                    <p className="text-sm text-primary mb-1 tracking-[0.2em] uppercase">step {activeStep + 1} of {applicationSteps.length}: <span className="tracking-normal text-sm">{applicationSteps[activeStep].label}</span></p>

                                    <p className='text-sm mb-1 mt-5'>Applicant's previous schools</p>
                                    <p className='text-sm text-gray-500'>Please fill the form below with the applicant's school. click on the "add school" button to add more if applicable</p>
                                    {validationErrors.languages && <p className='text-xs text-red-600 mb-2 mt-3'>Please add at least one language</p>}
                                    {schools.map((school, schoolIndex) => (<div key={schoolIndex} className={`mt-4 ${schoolIndex > 0 && 'pt-6 border-t border-gray-300'}`}>

                                        {schoolIndex > 0 && <div className="flex flex-row-reverse mb-3">
                                            <button onClick={()=>{removeSchool(schoolIndex)}} className='text-gray-500 transition duration-200 hover:text-red-600'><TrashIcon className={`w-5 h-5`} /></button>
                                        </div>}
                                        <div className='w-full'>
                                            <TextField
                                                inputLabel="School name" 
                                                fieldId={`school-name-${schoolIndex}`}
                                                requiredField={schoolIndex === 0 ? true : false}
                                                inputType="text" 
                                                preloadValue={school.school}
                                                hasError={validationErrors && validationErrors[`school-name-${schoolIndex}`]} 
                                                returnFieldValue={(value)=>{updateSchool(schoolIndex, 'school', value)}}
                                            />
                                        </div>
                                        <div className='w-full my-4'>
                                            <Checkbox
                                                CheckboxLabel="Check this box if this is the applicant's current school"
                                                // checkboxToggleFunction={()=>{setOverrideKycName(!overrideKycName)}} 
                                                checkboxToggleFunction={()=>{
                                                    updateSchool(schoolIndex, 'current', !school.current)
                                                }} 
                                                isChecked={school.current} 
                                                hasError={false} 
                                            />
                                        </div>

                                        <div className='w-full mt-4'>
                                            <p className='text-sm mb-1 mt-5'>Years attended</p>
                                            <div className='flex items-start justify-between mt-2 mb-4 gap-x-4'>
                                                <div className='w-full'>
                                                    <TextField
                                                        inputLabel="Start year" 
                                                        fieldId={`start-year-${schoolIndex}`}
                                                        requiredField={true}
                                                        inputType="text" 
                                                        preloadValue={school.startYear || ''}
                                                        hasError={validationErrors && validationErrors[`school-from-${schoolIndex}`]} 
                                                        returnFieldValue={(value)=>{updateSchool(schoolIndex, 'fromYear', value)}}
                                                    />
                                                </div>
                                                {!school.current && <div className='w-full'>
                                                    <TextField
                                                        inputLabel="End year" 
                                                        fieldId={`end-year-${schoolIndex}`}
                                                        requiredField={!school.current ? true : false}
                                                        inputType="text" 
                                                        preloadValue={school.endYear || ''}
                                                        hasError={validationErrors && validationErrors[`school-to-${schoolIndex}`]} 
                                                        returnFieldValue={(value)=>{updateSchool(schoolIndex, 'toYear', value)}}
                                                    />
                                                </div>}

                                            </div>
                                        </div>

                                        <div className='mt-4'>
                                            <RadioGroup 
                                                items={schoolTypes} 
                                                preSelectedIndex={schoolTypes.findIndex(type => type.value === school.type)}
                                                inputLabel={'School type'}
                                                requiredField={true}
                                                returnSelected={(value) => {updateSchool(schoolIndex, 'type', value.value)}} 
                                                hasError={validationErrors[`school-type-${schoolIndex}`]}  
                                                titleField='label'
                                            />
                                        </div>

                                        <div className='mt-4'>
                                            <TextField
                                                inputLabel="Latest/Current class" 
                                                fieldId={`latest-class-${schoolIndex}`}
                                                requiredField={true}
                                                inputType="text" 
                                                preloadValue={school.latestClass}
                                                hasError={validationErrors && validationErrors[`school-class-${schoolIndex}`]} 
                                                returnFieldValue={(value)=>{updateSchool(schoolIndex, 'latestClass', value)}}
                                            />
                                        </div>

                                        {/* {languageIndex > 0 && <div className='pb-[10px]'>
                                            <button onClick={()=>{removeLanguage(languageIndex)}} className='text-gray-500 transition duration-200 hover:text-red-600'><TrashIcon className={`w-5 h-5`} /></button>
                                        </div>} */}
                                    </div>))}
                                    <div className='flex flex-row-reverse w-full mt-3'>
                                        <button onClick={()=>{addSchool()}} className='text-gray-300 bg-vcm-dark-gray p-2 rounded transition duration-200 hover:bg-vcm-light-purple hover:text-vcm-dark-gray text-sm flex items-center gap-x-2'>
                                        <PlusIcon className={`w-5 h-5`} /> Add another school
                                        </button>
                                    </div>
                                </div>

                                <div className='mt-8 flex items-center justify-between'>
                                        <button onClick={()=>{setActiveStep(2)}} className='flex items-center gap-x-2 text-gray-500 hover:text-vcm-purple transition duration-200 mb-3 text-sm'>
                                            <ArrowIcon className={`w-4 h-4 rotate-180`} />
                                            Previous step
                                        </button>
                                        <div className='w-max'>
                                            <FormButton buttonLabel={
                                                <span className='flex items-center justify-center gap-x-4'>
                                                    Save & Continue <ArrowIcon className={`w-5 h-5`}/>
                                                </span>
                                            } buttonAction={()=>{completeStepFour()}} processing={applicationsSelector.updatingApplication} />
                                        </div>
                                    </div>
                            </div>
                        }

                        {activeStep === 4 && 
                            <div className='w-full lg:w-11/12 mx-auto'>
                                <div>
                                    <p className="text-sm text-primary mb-1 tracking-[0.2em] uppercase">step {activeStep + 1} of {applicationSteps.length}: <span className="tracking-normal text-sm">{applicationSteps[activeStep].label}</span></p>

                                    <div className='mt-8 w-full'>
                                    {(!applicationPayload.passportPhotograph || changingDocument === 'passport') && 
                                        <div className='relative'>
                                            {changingDocument === 'passport' && <button onClick={()=>{setChangingDocument(null)}} className='z-50 flex items-center gap-x-2 text-red-600 text-sm absolute top-[10px] right-[10px]'><CloseIcon className={`w-4 h-4`} /> Cancel</button>}
                                            <FileUpload
                                                hasError={validationErrors.passport && validationErrors.passport === true}
                                                fieldLabel={`Applicant passport photograph`}
                                                requiredField={true}
                                                returnFileDetails={(details)=>{
                                                    setPassportFile(details)
                                                }}
                                                acceptedFormats={['jpeg', 'jpg', 'png']}
                                            />
                                            <label className='block mt-3 text-xs text-gray-400'>{`Recent passport photograph of the applicant`}</label>
                                        </div>
                                        }

                                        {applicationPayload.passportPhotograph && changingDocument !== 'passport' && 
                                            <div className='mt-4 p-4 bg-gray-100 rounded border border-gray-400'>
                                            {(docType(applicationPayload.passportPhotograph) === 'png' || docType(applicationPayload.passportPhotograph) === 'jpg' || docType(applicationPayload.passportPhotograph) === 'jpeg')
                                                ?
                                                <img alt='existing document' src={applicationPayload.passportPhotograph} className='w-3/12' />
                                                :
                                                <img alt='existing document' src={DocumentSvgIcon} className='w-2/12' />
                                            }
                                            <p className='text-sm text-gray-500 mt-2'><span className='font-medium'>Passport photograph</span> - {docName(applicationPayload.passportPhotograph)}</p>
                                            <button onClick={()=>{setChangingDocument('passport')}} className='text-sm text-purple-800 hover:text-vcm-purple mt-1 flex items-center gap-x-1'>
                                                <LeftRightArrow className={`w-4 h-4`} />
                                                Change file</button>
                                            </div>
                                        }

                                    </div>

                                    {admission.requiredDocuments.map((doc, docIndex)=>(
                                        
                                        <div key={docIndex} className='mt-8 w-full'>
                                        {(!existingDocument(doc.name) || changingDocument === doc.name) && 
                                            <div className='relative'>
                                            {changingDocument === doc.name && <button onClick={()=>{setChangingDocument(null)}} className='z-50 flex items-center gap-x-2 text-red-600 text-sm absolute top-[10px] right-[10px]'><CloseIcon className={`w-4 h-4`} /> Cancel</button>}
                                                <FileUpload
                                                    hasError={validationErrors[`${docIndex}-doc`] === true}
                                                    fieldLabel={doc.name}
                                                    returnFileDetails={(details)=>{
                                                        addFile(doc, details)
                                                    }}
                                                    requiredField={doc.required && doc.required === true}
                                                    acceptedFormats={['pdf', 'jpeg', 'jpg', 'png']}
                                                />
                                                <label className='block mt-3 text-xs text-gray-400'>{doc.description}</label>
                                            </div>
                                        }

                                            {existingDocument(doc.name) && changingDocument !== doc.name && 
                                                <div className='mt-4 p-4 bg-gray-100 rounded border border-gray-400'>
                                                {(docType(existingDocument(doc.name).url ) === 'png' || docType(existingDocument(doc.name).url ) === 'jpg' || docType(existingDocument(doc.name).url ) === 'jpeg')
                                                    ?
                                                    <img alt='existing document' src={existingDocument(doc.name).url} className='w-3/12' />
                                                    :
                                                    <img alt='existing document' src={DocumentSvgIcon} className='w-2/12' />
                                                }
                                                <p className='text-sm text-gray-500 mt-2'><span className='font-medium'>{doc.name}</span> - {docName(existingDocument(doc.name).url)}</p>
                                                <button onClick={()=>{setChangingDocument(doc.name)}} className='text-sm text-purple-800 hover:text-vcm-purple mt-1 flex items-center gap-x-1'>
                                                    <LeftRightArrow className={`w-4 h-4`} />
                                                    Change file</button>
                                                </div>
                                            }
                                        </div>
                                        

                                    ))}
                                </div>

                                <div className='mt-8 flex items-center justify-between'>
                                        <button onClick={()=>{setActiveStep(3)}} className='flex items-center gap-x-2 text-gray-500 hover:text-vcm-purple transition duration-200 mb-3 text-sm'>
                                            <ArrowIcon className={`w-4 h-4 rotate-180`} />
                                            Previous step
                                        </button>
                                        <div className='w-max'>
                                            <FormButton buttonLabel={
                                                <span className='flex items-center justify-center gap-x-4'>
                                                    Save & Continue <ArrowIcon className={`w-5 h-5`}/>
                                                </span>
                                            } buttonAction={()=>{completeStepFive()}} processing={applicationsSelector.updatingApplication || processing} />
                                        </div>
                                    </div>
                            </div>
                        }

                        {activeStep === 5 && 
                            <div className='w-full lg:w-11/12 mx-auto'>
                                <div>
                                    <p className="text-sm text-primary mb-1 tracking-[0.2em] uppercase">Declaration & verification</p>

                                    <p className='text-sm text-gray-500'>Thank you. We've received all the information we need for this application</p>
                                    <p className='text-sm text-gray-500 mt-4'>You can review your application by using the sidebar on the left to navigate between the steps and update the information where needed (you will not be able to update the application after submitting). </p>
                                    <p className='font-[550] text-sm mt-4'>When you're ready please read through and check to accept the following declarations.</p>
                                    <p className='font-[400] text-sm mt-4 pt-8 border-t'>I/We, being the parent/parents of or being the person/persons having parental rights in respect of the applicant hereby</p>

                                    {/* <div className='w-full my-4 pt-8 border-t'>
                                        <Checkbox
                                            CheckboxLabel="I hereby declare that the information provided here is, to the best of my knowledge, true, and that I have the legal right to apply for admission on behalf of the applicant."
                                            // checkboxToggleFunction={()=>{setOverrideKycName(!overrideKycName)}} 
                                            checkboxToggleFunction={()=>{
                                                setAttested(!attested)
                                            }} 
                                            isChecked={attested} 
                                            hasError={false} 
                                        />
                                    </div> */}
                                    {declarations.map((declaration, declarationIndex)=>(<div key={declarationIndex} className='w-full my-6'>
                                        <Checkbox
                                            CheckboxLabel={declaration.description}
                                            // checkboxToggleFunction={()=>{setOverrideKycName(!overrideKycName)}} 
                                            checkboxToggleFunction={()=>{
                                                toggleDeclaration(declarationIndex)
                                            }} 
                                            isChecked={declaration.selected} 
                                            hasError={validationErrors[`declaration-${declarationIndex}`]} 
                                        />
                                    </div>))}
                                </div>

                                {examinationCentersSelector?.examinationCenters?.examinationCenters && <div className='mt-8 pt-8 border-t'>
                                    <p className="text-sm text-primary mb-1 tracking-[0.2em] uppercase">Examination Center</p>

                                    <p className='text-sm text-primary'>You can choose one of our examination centers closest to you if you want to. We will schedule exams for the applicant in the location you chose</p>

                                    <div className='mt-4'>
                                        <RadioGroup 
                                            items={examinationCentersSelector?.examinationCenters?.examinationCenters.map(center =>{ return {
                                                label: center.name,
                                                value: center._id,
                                                description: `${center.address}, ${center.city}, ${center.state} ${center.state !== 'Abuja' ? 'State' : ''}`
                                            }})} 
                                            preSelectedIndex={''}
                                            inputLabel={'Select Preferred Center'}
                                            requiredField={true}
                                            returnSelected={(value) => {setPreferredCenter(value)}} 
                                            hasError={validationErrors.preferredCenter}  
                                            showDescriptions={true}
                                            titleField='label'
                                        />
                                    </div>
                                </div>}

                                <p className="text-sm text-primary mb-1 tracking-[0.2em] uppercase mt-8">Additional Information</p>

                                <p className='text-sm text-primary mb-2'>Are there any circumstances, medical or otherwise, which might require the applicant to be considered for special assistance at VAATIA COLLEGE MAKURDI, or of which the College should be aware and which might affect its judgment of this application? Please include details of any disability.</p>

                                <div className='w-full my-5'>
                                    <TextareaField
                                        inputLabel="More Information" 
                                        fieldId="more-info" 
                                        requiredField={false}
                                        inputPlaceholder={`You can add any additional information here. Please make it as detailed as possible`}
                                        inputType="text" 
                                        preloadValue={''}
                                        hasError={false} 
                                        returnFieldValue={(value)=>{setAdditionalInformation(value)}}
                                    />
                                </div>

                                <div className='w-full my-5'>
                                    <FileUpload
                                        hasError={false}
                                        fieldLabel={`Supporting documents`}
                                        requiredField={false}
                                        returnFileDetails={(details)=>{
                                            setSupportingDocument(details)
                                        }}
                                        acceptedFormats={['pdf', 'jpeg', 'jpg', 'png']}
                                    />
                                    <label className='block mt-3 text-xs text-gray-400'>{`Please add any available documents supporting the statement made above.`}</label>
                                </div>


                                <div className='mt-8 flex items-center justify-between pt-8 border-t'>
                                    <button onClick={()=>{setActiveStep(4)}} className='flex items-center gap-x-2 text-gray-500 hover:text-vcm-purple transition duration-200 mb-3 text-sm'>
                                        <ArrowIcon className={`w-4 h-4 rotate-180`} />
                                        Previous step
                                    </button>
                                    <div className='w-max'>
                                        <FormButton buttonLabel={
                                            <span className='flex items-center justify-center gap-x-4'>
                                                Submit application <CheckIcon className={`w-5 h-5`}/>
                                            </span>
                                        } buttonAction={()=>{submitApplication()}} processing={applicationsSelector.updatingApplication} />
                                    </div>
                                </div>
                            </div>
                        }
                    </div>

                    <div className='hidden xl:block w-full xl:w-3/12'>
                        <div className=' w-full p-4 rounded border border-vcm-purple bg-vcm-light-purple bg-opacity-20'>
                            <p className='font-medium text-sm'>The Academic year starts in <span className='font-[600] text-vcm-purple'>{moment(admission.academicSession.startDate).format('MMMM')}, {moment(admission.academicSession.startDate).format('YYYY')}</span></p>
                        </div>
                    </div>
                </div>
            }
        </UserLayout>
    )
}

export default NewApplication